import { forwardRef } from "react";

interface LibraryStrategyCardProps {
  category: string;
  title: string;
  analysisLogic: string;
  neutralizationScript: string;
  toxicityScore: number;
}

const LibraryStrategyCard = forwardRef<HTMLDivElement, LibraryStrategyCardProps>(
  ({ category, title, analysisLogic, neutralizationScript, toxicityScore }, ref) => {
    const isHigh = toxicityScore >= 7;
    const isMod = toxicityScore >= 4;
    const accentColor = isHigh ? "#ef4444" : isMod ? "#f59e0b" : "#64748b";
    const cyanAccent = "#00f2ff";

    return (
      <div
        ref={ref}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 1080,
          height: 1920,
          background: "#09090b",
          fontFamily: "'Inter', system-ui, sans-serif",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "80px 80px 64px",
        }}
      >
        {/* Ambient glow */}
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            width: 800,
            height: 800,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${cyanAccent}06 0%, transparent 65%)`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />

        {/* Top branding */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 48,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 14,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.50)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            GREYROCKAI
          </span>
          <span
            style={{
              fontSize: 12,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.18)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            FORENSIC LIBRARY
          </span>
        </div>

        {/* Category pill */}
        <div style={{ marginBottom: 24, flexShrink: 0 }}>
          <span
            style={{
              display: "inline-block",
              padding: "10px 28px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: cyanAccent,
              background: "rgba(0,242,255,0.08)",
              border: `1px solid rgba(0,242,255,0.25)`,
            }}
          >
            {category}
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: 52,
            fontWeight: 800,
            lineHeight: 1.15,
            color: "#ffffff",
            margin: "0 0 16px 0",
            letterSpacing: "-0.02em",
            flexShrink: 0,
          }}
        >
          {title}
        </h1>

        {/* Severity indicator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 56,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: accentColor,
              display: "inline-block",
            }}
          />
          <span
            style={{
              fontSize: 14,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: "0.1em",
              color: accentColor,
              textTransform: "uppercase",
            }}
          >
            Forensic Score: {Number(toxicityScore ?? 0).toFixed(1)}/10
          </span>
        </div>

        {/* Divider */}
        <div
          style={{
            width: "100%",
            height: 1,
            background: "rgba(255,255,255,0.08)",
            marginBottom: 48,
            flexShrink: 0,
          }}
        />

        {/* Forensic Analysis Section */}
        <div style={{ marginBottom: 48, flex: "0 1 auto" }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: cyanAccent,
              marginBottom: 16,
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
            }}
          >
            Forensic Analysis
          </div>
          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderLeft: `3px solid ${cyanAccent}`,
              borderRadius: 8,
              padding: "24px 28px",
            }}
          >
            <p
              style={{
                fontSize: 20,
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.82)",
                margin: 0,
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              {analysisLogic.slice(0, 400)}
              {analysisLogic.length > 400 ? "…" : ""}
            </p>
          </div>
        </div>

        {/* Neutralization Strategy Section */}
        <div style={{ marginBottom: 48, flex: "0 1 auto" }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: cyanAccent,
              marginBottom: 16,
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
            }}
          >
            Neutralization Strategy
          </div>
          <div
            style={{
              background: "rgba(0,242,255,0.04)",
              border: "1px solid rgba(0,242,255,0.15)",
              borderRadius: 8,
              padding: "24px 28px",
            }}
          >
            <p
              style={{
                fontSize: 20,
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.78)",
                margin: 0,
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 400,
              }}
            >
              {neutralizationScript.slice(0, 400)}
              {neutralizationScript.length > 400 ? "…" : ""}
            </p>
          </div>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Bottom CTA watermark */}
        <div
          style={{
            textAlign: "center",
            flexShrink: 0,
            paddingTop: 32,
          }}
        >
          <span
            style={{
              fontSize: 16,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.60)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            ANALYZE YOURS → GREYROCKAI.APP®
          </span>
        </div>
      </div>
    );
  }
);

LibraryStrategyCard.displayName = "LibraryStrategyCard";

export default LibraryStrategyCard;
